import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';

import { Params } from '@angular/router';
import { ProviderLatestConfigService } from '@app/rp/services/provider/provider-latest-config.service';
import { LoadingScreenResponse } from '@app/rp/treatment-proposal/models/loading-screen.response';
import { ErrorResponse, GenericListResponse, PaymentUnsubmittedItemResponse } from '@core/models';
import { ApiUrlService } from '@core/services/api-url.service';
import { HelperService } from '@core/services/helper.service';
import { environment } from '@environments/environment';
import { format } from 'date-fns';
import { catchError, filter, map } from 'rxjs/operators';
import { RpJourneyProviderService } from '../../rp/services/provider/provider.service';
import { ModuleType } from '../enums/module-type.enum';
import { UnsubmittedPlan } from '../models/common/sms-journey.interface';
import { AddressLookUpResponse } from '../models/payment/address.interface';
import { BsbValidationResponse } from '../models/payment/bsb-validation.interface';
import { SendSmsQueryParams } from '../models/payment/sms.interface';
import { UserSettingsService } from './user-settings/user-settings.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  unsubmittedPlansSubject = new BehaviorSubject<PaymentUnsubmittedItemResponse[]>([]);

  constructor(
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private helperService: HelperService,
    private userSettingsService: UserSettingsService,
    private rpJourneyProviderService: RpJourneyProviderService,
    private providerLatestConfigService: ProviderLatestConfigService
  ) {}

  getPDF(key: string): Observable<GenericListResponse<{ files: string }, HttpErrorResponse>> {
    return this.httpClient
      .get<GenericListResponse<{ files: string }, HttpErrorResponse>>(
        this.apiUrlService.paymentPlanSummaryUrl + `/${key}/download`
      )
      .pipe(
        filter(response => !!response?.data[0]),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      );
  }

  smsPlan(id: string, options: SendSmsQueryParams): Observable<any> {
    let params = new HttpParams();
    params = params.append('company_brand', environment.company_brand);

    if (options?.ddrSource) {
      params = params.append('ddr_source', options.ddrSource);
    }

    if (options?.checkExpiry) {
      params = params.append('check_expiry', options.checkExpiry);
    }

    if (options?.moduleType) {
      params = params.append('custrecord_module_type', options.moduleType);
    }

    return this.httpClient
      .get<GenericListResponse<{ message: string }, any>>(
        `${this.apiUrlService.smsSavedPlanUrl}/${id}`,
        {
          params
        }
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('SMS unsuccessful', error);
          return throwError(error);
        })
      );
  }

  approveOrDecline(
    id: string,
    request: { action: string; isApprovalPopUp?: boolean }
  ): Observable<GenericListResponse<{ message: string }, HttpErrorResponse>> {
    return this.httpClient
      .post<GenericListResponse<{ message: string }, HttpErrorResponse>>(
        this.apiUrlService.paymentPlanApprovalUrl + `/${id}`,
        request
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('approveOrDecline unsuccessful', error);
          return throwError(error.error);
        })
      );
  }

  prepare(id: string) {
    sessionStorage.setItem('preparedPlan', id + '');
  }

  retrievePlan(params: Params): Observable<UnsubmittedPlan> {
    const appendParams = this.getRetrievePlanParams(params);
    return this.httpClient.get(this.apiUrlService.savedPlanNoLoginUrl + '/' + appendParams).pipe(
      map((data: any) => {
        if (this.helperService.checkAPIResponse(data)) {
          data = this.helperService.getAPIDataResponse(data);

          sessionStorage.setItem('form_fields', JSON.stringify(data.form_fields));
          this.rpJourneyProviderService.setProviderData(data.provider_details);
          this.providerLatestConfigService.setLatestConfig(data.provider_details.settings_latest);
          this.userSettingsService.setCurrentProviderSettings(data.provider_details?.settings);

          return data;
        }
      })
    );
  }

  addressLookup(term: string): Observable<AddressLookUpResponse[]> {
    const requestPayload = {
      payload: [
        {
          fullAddress: term
        }
      ],
      sourceOfTruth: 'AUSOTS'
    };

    return this.httpClient.post(this.apiUrlService.addressLookupUrl, requestPayload).pipe(
      map((data: any) => {
        return data?.payload || [];
      })
    );
  }

  checkBSB(request: { bsbNumber: string }, urlParams: string): Observable<BsbValidationResponse[]> {
    const bsbUrl =
      urlParams === ''
        ? this.apiUrlService.bsbCheckUrl
        : this.apiUrlService.bsbCheckNoLoginUrl + '/' + urlParams;

    return this.httpClient.post(bsbUrl, request).pipe(
      map((data: any) => {
        if (this.helperService.checkAPIResponse(data)) {
          return this.helperService.getAPIDataResponse(data);
        }
        return data;
      })
    );
  }

  retrieveProposalLoaderDetails(
    ddrDraftId: string,
    providerName: string
  ): Observable<LoadingScreenResponse> {
    return this.httpClient
      .get<GenericListResponse<LoadingScreenResponse, ErrorResponse>>(
        `${this.apiUrlService.savedPlanNoLoginUrl}/proposal-loading-screen/${ddrDraftId}?providerName=${providerName}`
      )
      .pipe(
        map(data => {
          return data.data[0];
        })
      );
  }

  transformToApiModel(
    pendingPlanModel: PaymentUnsubmittedItemResponse
  ): PaymentUnsubmittedItemResponse {
    if (!pendingPlanModel.json_transformed) {
      return pendingPlanModel;
    }

    if (pendingPlanModel.custrecord_module_type === ModuleType.Amendment) {
      const formattedDate = format(
        new Date(pendingPlanModel.json_transformed.start_date),
        'dd/MM/yyyy'
      );
      pendingPlanModel.json_transformed.start_date = formattedDate;
    }

    return pendingPlanModel;
  }

  private getRetrievePlanParams(params: Params): string {
    let appendParams = params['ddrDraftId'] + '?expires=' + params['expires'];

    if (params['payload_encrypt']) {
      appendParams = appendParams + '&payload_encrypt=' + params['payload_encrypt'];
    }

    if (params['source']) {
      appendParams = appendParams + '&source=' + params['source'];
    }

    appendParams = appendParams + '&signature=' + params['signature'];

    return appendParams;
  }

}
